"use client";

import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import ZeekMapsIcon from "../ui/icons/ZeekMapsIcon";

// --- Types ---
export type AnimationPhase = "scatter" | "line" | "circle" | "bottom-strip";

interface FlipCardProps {
	src: string;
	index: number;
	total?: number;
	phase?: AnimationPhase;
	target: {
		x: number;
		y: number;
		rotation: number;
		scale: number;
		opacity: number;
	};
}

// --- FlipCard Component ---
const IMG_WIDTH = 100; // Reduced from 100
const IMG_HEIGHT = 140; // Reduced from 140

function FlipCard({ src, index, target }: FlipCardProps) {
	return (
		<motion.div
			// Smoothly animate to the coordinates defined by the parent
			animate={{
				x: target.x,
				y: target.y,
				rotate: target.rotation,
				scale: target.scale,
				opacity: target.opacity,
			}}
			transition={{
				type: "spring",
				stiffness: 40,
				damping: 15,
			}}
			// Initial style
			style={{
				position: "absolute",
				width: IMG_WIDTH,
				height: IMG_HEIGHT,
				transformStyle: "preserve-3d", // Essential for the 3D hover effect
				perspective: "1000px",
			}}
			className="cursor-pointer group "
		>
			<motion.div
				className="relative h-20 w-20 md:h-full md:w-full"
				style={{ transformStyle: "preserve-3d" }}
				transition={{
					duration: 0.6,
					type: "spring",
					stiffness: 260,
					damping: 20,
				}}
				whileHover={{ rotateY: 180 }}
			>
				{/* Front Face */}
				<div
					className="absolute inset-0 overflow-hidden rounded-xl shadow-lg bg-gray-200"
					style={{ backfaceVisibility: "visible" }}
				>
					<Image
						src={src}
						alt={`hero-${index}`}
						className="h-full w-full object-cover"
						width={IMG_WIDTH}
						height={IMG_HEIGHT}
					/>
					<div className="absolute inset-0 bg-black/10 transition-colors group-hover:bg-transparent" />
				</div>
			</motion.div>
		</motion.div>
	);
}

// --- Main Hero Component ---
const TOTAL_IMAGES = 10;
const MAX_SCROLL = 3000; // Virtual scroll range

// Unsplash Images
const IMAGES = [
	"/images/display/1.jpg",
	"/images/display/2.jpg",
	"/images/display/3.jpg",
	"/images/display/4.jpg",
	"/images/display/5.jpg",
	"/images/display/6.jpg",
	"/images/display/7.jpg",
	"/images/display/8.jpg",
	"/images/display/9.jpg",
	"/images/display/10.jpg",
];

// Helper for linear interpolation
const lerp = (start: number, end: number, t: number) =>
	start * (1 - t) + end * t;

// Generate random scatter positions once at module level
const scatterPositions = IMAGES.map(() => ({
	x: (Math.random() - 0.5) * 1500,
	y: (Math.random() - 0.5) * 1000,
	rotation: (Math.random() - 0.5) * 180,
	scale: 0.6,
	opacity: 0,
}));

export default function IntroAnimation() {
	const [introPhase, setIntroPhase] = useState<AnimationPhase>("scatter");
	const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
	const containerRef = useRef<HTMLDivElement>(null);

	// --- Container Size ---
	useEffect(() => {
		if (!containerRef.current) return;

		const handleResize = (entries: ResizeObserverEntry[]) => {
			for (const entry of entries) {
				setContainerSize({
					width: entry.contentRect.width,
					height: entry.contentRect.height,
				});
			}
		};

		const observer = new ResizeObserver(handleResize);
		observer.observe(containerRef.current);

		// Initial set
		setContainerSize({
			width: containerRef.current.offsetWidth,
			height: containerRef.current.offsetHeight,
		});

		return () => observer.disconnect();
	}, []);

	// --- Virtual Scroll Logic (Page Scroll) ---
	const virtualScroll = useMotionValue(0);

	useEffect(() => {
		const handleScroll = () => {
			const scrollY = window.scrollY;
			// Map page scroll to virtual scroll range
			// Adjust the multiplier to control how much page scroll affects the animation
			const mappedScroll = Math.min(scrollY * 2, MAX_SCROLL);
			virtualScroll.set(mappedScroll);
		};

		window.addEventListener("scroll", handleScroll, { passive: true });
		// Initial set
		handleScroll();

		return () => window.removeEventListener("scroll", handleScroll);
	}, [virtualScroll]);

	// 1. Morph Progress: 0 (Circle) -> 1 (Bottom Arc)
	// Happens between scroll 0 and 600
	const morphProgress = useTransform(virtualScroll, [0, 600], [0, 1]);
	const smoothMorph = useSpring(morphProgress, { stiffness: 40, damping: 20 });

	// 2. Scroll Rotation (Shuffling): Starts after morph (e.g., > 600)
	// Rotates the bottom arc as user continues scrolling
	const scrollRotate = useTransform(virtualScroll, [600, 3000], [0, 360]);
	const smoothScrollRotate = useSpring(scrollRotate, {
		stiffness: 40,
		damping: 20,
	});

	// --- Mouse Parallax ---
	const mouseX = useMotionValue(0);
	const smoothMouseX = useSpring(mouseX, { stiffness: 30, damping: 20 });

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		const handleMouseMove = (e: MouseEvent) => {
			const rect = container.getBoundingClientRect();
			const relativeX = e.clientX - rect.left;

			// Normalize -1 to 1
			const normalizedX = (relativeX / rect.width) * 2 - 1;
			// Move +/- 100px
			mouseX.set(normalizedX * 100);
		};
		container.addEventListener("mousemove", handleMouseMove);
		return () => container.removeEventListener("mousemove", handleMouseMove);
	}, [mouseX]);

	// --- Intro Sequence ---
	useEffect(() => {
		const timer1 = setTimeout(() => setIntroPhase("line"), 500);
		const timer2 = setTimeout(() => setIntroPhase("circle"), 2500);
		return () => {
			clearTimeout(timer1);
			clearTimeout(timer2);
		};
	}, []);

	// --- Render Loop (Manual Calculation for Morph) ---
	const [morphValue, setMorphValue] = useState(0);
	const [rotateValue, setRotateValue] = useState(0);
	const [parallaxValue, setParallaxValue] = useState(0);

	useEffect(() => {
		const unsubscribeMorph = smoothMorph.on("change", setMorphValue);
		const unsubscribeRotate = smoothScrollRotate.on("change", setRotateValue);
		const unsubscribeParallax = smoothMouseX.on("change", setParallaxValue);
		return () => {
			unsubscribeMorph();
			unsubscribeRotate();
			unsubscribeParallax();
		};
	}, [smoothMorph, smoothScrollRotate, smoothMouseX]);

	// --- Content Opacity ---
	// Fade in content when arc is formed (morphValue > 0.8)
	const contentOpacity = useTransform(smoothMorph, [0.8, 1], [0, 1]);
	const contentY = useTransform(smoothMorph, [0.8, 1], [20, 0]);

	return (
		<div
			ref={containerRef}
			className="relative w-full min-h-screen overflow-hidden"
		>
			{/* Container */}
			<div className="flex h-full w-full flex-col items-center justify-center perspective-1000">
				{/* Intro Text (Fades out) */}
				<div className="absolute top-1/2 z-0 flex flex-col items-center justify-center text-center -translate-y-1/2">
					<motion.h1
						initial={{ opacity: 0, y: 20, filter: "blur(10px)" }}
						animate={
							introPhase === "circle" && morphValue < 0.5
								? { opacity: 1 - morphValue * 2, y: 0, filter: "blur(0px)" }
								: { opacity: 0, filter: "blur(10px)" }
						}
						transition={{ duration: 1 }}
						className="flex flex-col items-center gap-2 text-lg lg:text-2xl font-semibold tracking-tight text-primary/80"
					>
						<ZeekMapsIcon className="w-12 h-12" />
						The future is built on Maps.
					</motion.h1>
					<motion.p
						initial={{ opacity: 0 }}
						animate={
							introPhase === "circle" && morphValue < 0.5
								? { opacity: 0.5 - morphValue }
								: { opacity: 0 }
						}
						transition={{ duration: 1, delay: 0.2 }}
						className="mt-2 text-xs font-bold tracking-[0.2em] text-primary/80"
					>
						SCROLL TO EXPLORE
					</motion.p>
				</div>

				{/* Arc Active Content (Fades in) */}
				<motion.div
					style={{ opacity: contentOpacity, y: contentY }}
					className="absolute top-1/2 z-10 flex flex-col items-center justify-center text-center -translate-y-1/2 bg-background/50 rounded-full shadow-lg p-6 backdrop-blur-sm"
				>
					<ZeekMapsIcon className="w-12 h-12 mb-2" />
					<h2 className="text-xl md:text-3xl font-semibold text-primary tracking-tight mb-4">
						Explore
					</h2>
					<p className="text-sm text-primary/80 max-w-xs leading-relaxed">
						Discover the power of real-time location intelligence with Zeek
						Maps.
					</p>
				</motion.div>

				{/* Main Container */}
				<div className="absolute flex items-center justify-center w-full h-full top-1/2 -translate-y-1/2">
					{IMAGES.slice(0, TOTAL_IMAGES).map((src, i) => {
						let target = { x: 0, y: 0, rotation: 0, scale: 1, opacity: 1 };

						// 1. Intro Phases (Scatter -> Line)
						if (introPhase === "scatter") {
							target = scatterPositions[i];
						} else if (introPhase === "line") {
							const lineSpacing = 70; // Adjusted for smaller images (60px width + 10px gap)
							const lineTotalWidth = TOTAL_IMAGES * lineSpacing;
							const lineX = i * lineSpacing - lineTotalWidth / 2;
							target = { x: lineX, y: 0, rotation: 0, scale: 1, opacity: 1 };
						} else {
							// 2. Circle Phase & Morph Logic

							// Responsive Calculations
							const isMobile = containerSize.width < 768;
							const minDimension = Math.min(
								containerSize.width,
								containerSize.height,
							);

							// A. Calculate Circle Position
							const circleRadius = Math.min(minDimension * 0.35, 350);

							const circleAngle = (i / TOTAL_IMAGES) * 360;
							const circleRad = (circleAngle * Math.PI) / 180;
							const circlePos = {
								x: Math.cos(circleRad) * circleRadius,
								y: Math.sin(circleRad) * circleRadius,
								rotation: circleAngle + 90,
							};

							// B. Calculate Bottom Arc Position
							// "Rainbow" Arch: Convex up. Center is highest point.

							// Radius:
							const baseRadius = Math.min(
								containerSize.width,
								containerSize.height * 1.5,
							);
							const arcRadius = baseRadius * (isMobile ? 1.4 : 1.1);

							// Position:
							const arcApexY = containerSize.height * (isMobile ? 0.35 : 0.25);
							const arcCenterY = arcApexY + arcRadius;

							// Spread angle:
							const spreadAngle = isMobile ? 100 : 130;
							const startAngle = -90 - spreadAngle / 2;
							const step = spreadAngle / (TOTAL_IMAGES - 1);

							// Apply Scroll Rotation (Shuffle) with Bounds
							// We want to clamp rotation so images don't disappear.
							// Map scroll range [600, 3000] to a limited rotation range.
							// Range: [-spreadAngle/2, spreadAngle/2] keeps them roughly in view.
							// We map 0 -> 1 (progress of scroll loop) to this range.

							// Note: rotateValue comes from smoothScrollRotate which maps [600, 3000] -> [0, 360]
							// We need to adjust that mapping in the hook above, OR adjust it here.
							// Better to adjust it here relative to the spread.

							// Let's interpret rotateValue (0 to 360) as a progress 0 to 1
							const scrollProgress = Math.min(
								Math.max(rotateValue / 360, 0),
								1,
							);

							// Calculate bounded rotation:
							// Move from 0 (centered) to -spreadAngle (all the way left) or similar.
							// Let's allow scrolling through the list.
							// Total sweep needed to see all items if we start at one end?
							// If we start centered, we can go +/- spreadAngle/2.

							// User wants to "stop on the last image".
							// Let's map scroll to: 0 -> -spreadAngle (shifts items left)
							const maxRotation = spreadAngle * 0.8; // Don't go all the way, keep last item visible
							const boundedRotation = -scrollProgress * maxRotation;

							const currentArcAngle = startAngle + i * step + boundedRotation;
							const arcRad = (currentArcAngle * Math.PI) / 180;

							const arcPos = {
								x: Math.cos(arcRad) * arcRadius + parallaxValue,
								y: Math.sin(arcRad) * arcRadius + arcCenterY,
								rotation: currentArcAngle + 90,
								scale: isMobile ? 1.4 : 1.8, // Increased scale for active state
							};

							// C. Interpolate (Morph)
							target = {
								x: lerp(circlePos.x, arcPos.x, morphValue),
								y: lerp(circlePos.y, arcPos.y, morphValue),
								rotation: lerp(circlePos.rotation, arcPos.rotation, morphValue),
								scale: lerp(1, arcPos.scale, morphValue),
								opacity: 1,
							};
						}

						return (
							<FlipCard
								// biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
								key={i}
								src={src}
								index={i}
								total={TOTAL_IMAGES}
								phase={introPhase} // Pass intro phase for initial animations
								target={target}
							/>
						);
					})}
				</div>
			</div>
		</div>
	);
}
